import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons"
import { Button, Checkbox, Input, Select, Tag, Tooltip, Typography } from "antd"
import { debounce } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  CORE_SKILL_LEVEL_OPTIONS,
  DIGITAL_SKILL_LEVEL_OPTIONS,
  SCULPTURE_SKILL_LEVEL_OPTIONS,
} from "#constants/skillLevelConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  EVALUATION_STATUS,
  type EvaluationConstructor,
  NO_PROMO_REASON_LABELS,
  NO_PROMO_REASONS,
  type NoPromoReason,
} from "#customTypes/composedTypes/evaluationTypes"
import { type DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { getEvaluationStatusColor } from "#utils/evaluationHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import {
  getPromotionTag,
  promotionNeedsApprove,
  promotionReviewTone,
  promotionSubmitTooltip,
  showsHoldReason,
} from "#utils/promotionRules"

const COLUMN_WIDTH = {
  student: 210,
  skill: 148,
  currentSkill: 176,
  tag: 112,
  action: 144,
  person: 140,
} as const

const COLUMN_GAP = 8

/** Fixed columns plus the gaps between them. The class card sizes to this. */
export const PROMOTION_ROW_WIDTH =
  COLUMN_WIDTH.student +
  COLUMN_WIDTH.currentSkill +
  COLUMN_WIDTH.skill +
  COLUMN_WIDTH.tag +
  COLUMN_WIDTH.action +
  COLUMN_WIDTH.person * 2 +
  COLUMN_GAP * 6

type PromotionRowProps = {
  student: StudentConstructor | undefined
  classGroup: CLASS_GROUPS
  evaluation:
    | Pick<
        EvaluationConstructor,
        | "status"
        | "currentSkill"
        | "nextSkill"
        | "assignedTo"
        | "approverId"
        | "noPromoReason"
        | "noPromoText"
      >
    | undefined
  currentSkill: string
  canEdit: boolean
  canApprove: boolean
  canAssign: boolean
  canUpdateStudent: boolean
  approveNeedsStudentWrite: boolean
  noEvalNeedsStudentDelete: boolean
  teacherOptions: DropdownOption[]
  approverOptions: DropdownOption[]
  onCurrentSkill: (skill: string) => void
  onNextSkill: (skill: string) => void
  onToggleReason: (reason: NoPromoReason, checked: boolean) => void
  onNoteEditing: () => void
  onNote: (text: string) => void
  onSubmit: () => void
  onApprove: () => void
  onReject: () => void
  onNoEval: (checked: boolean) => void
  onAssignTeacher: (teacherId: string | null) => void
  onAssignApprover: (approverId: string | null) => void
}

const PromotionRow = ({
  student,
  classGroup,
  evaluation,
  currentSkill,
  canEdit,
  canApprove,
  canAssign,
  canUpdateStudent,
  approveNeedsStudentWrite,
  noEvalNeedsStudentDelete,
  teacherOptions,
  approverOptions,
  onCurrentSkill,
  onNextSkill,
  onToggleReason,
  onNoteEditing,
  onNote,
  onSubmit,
  onApprove,
  onReject,
  onNoEval,
  onAssignTeacher,
  onAssignApprover,
}: PromotionRowProps) => {
  const theme = useThemeContext()
  const status = evaluation?.status

  const skillLevelOptions = useMemo((): DropdownOption[] => {
    if (classGroup === CLASS_GROUPS.DIGITAL_ART) {
      return DIGITAL_SKILL_LEVEL_OPTIONS
    }
    if (classGroup === CLASS_GROUPS.SCULPTURE) {
      return SCULPTURE_SKILL_LEVEL_OPTIONS
    }
    return CORE_SKILL_LEVEL_OPTIONS
  }, [classGroup])

  const promotionTag = getPromotionTag(
    status,
    currentSkill || undefined,
    evaluation?.nextSkill,
    classGroup
  )

  const showHoldBlock =
    status !== EVALUATION_STATUS.NO_EVAL &&
    showsHoldReason(
      currentSkill || undefined,
      evaluation?.nextSkill,
      classGroup
    )

  const needsApprove = promotionNeedsApprove(status)
  const canClearNoEval = canEdit && status === EVALUATION_STATUS.NO_EVAL
  const [currentLevelUnlocked, setCurrentLevelUnlocked] = useState(false)
  const currentLevelLocked = Boolean(currentSkill) && !currentLevelUnlocked
  const canChangeCurrentLevel = canEdit && canUpdateStudent
  const approveBlocked = !canUpdateStudent && approveNeedsStudentWrite
  const noEvalBlocked =
    status !== EVALUATION_STATUS.NO_EVAL &&
    !canUpdateStudent &&
    noEvalNeedsStudentDelete
  const studentLevelTooltip =
    "You need permission to update this student's level"

  const submitTooltip = promotionSubmitTooltip({
    currentSkill,
    nextSkill: evaluation?.nextSkill,
    classGroup,
    status,
    noPromoReason: evaluation?.noPromoReason,
  })
  const submitDisabled = submitTooltip != null
  const approveDisabled = !needsApprove || approveBlocked
  const approveTooltip = approveBlocked
    ? studentLevelTooltip
    : "Nothing to approve"

  const [noteText, setNoteText] = useState(evaluation?.noPromoText ?? "")
  const noteBurstStartedRef = useRef(false)
  const onNoteRef = useRef(onNote)
  onNoteRef.current = onNote
  const onNoteEditingRef = useRef(onNoteEditing)
  onNoteEditingRef.current = onNoteEditing

  const debouncedOnNote = useMemo(
    () =>
      debounce((text: string) => {
        onNoteRef.current(text)
        noteBurstStartedRef.current = false
      }, 400),
    []
  )

  useEffect(
    () => () => {
      debouncedOnNote.cancel()
    },
    [debouncedOnNote]
  )

  useEffect(() => {
    if (noteBurstStartedRef.current) return
    setNoteText(evaluation?.noPromoText ?? "")
  }, [evaluation?.noPromoText])

  useEffect(() => {
    setCurrentLevelUnlocked(false)
  }, [currentSkill])

  const reviewTone = promotionReviewTone(status)
  const rowBackground =
    reviewTone === "approved"
      ? "#E5F3E6"
      : reviewTone === "rejected"
        ? "#F8E4E4"
        : !currentSkill
          ? "#FDECEC"
          : undefined
  const reviewEdge =
    reviewTone === "approved"
      ? theme.eric.green
      : reviewTone === "rejected"
        ? theme.eric.red
        : undefined
  const tagColor =
    promotionTag === "No eval"
      ? getEvaluationStatusColor(EVALUATION_STATUS.NO_EVAL)
      : promotionTag === "Promoted"
        ? theme.success
        : theme.warning

  return (
    <Flex
      col
      gap={4}
      style={{
        width: "100%",
        padding: "4px 0",
        marginBottom: 4,
        backgroundColor: rowBackground,
        boxShadow: reviewEdge ? `inset 4px 0 0 ${reviewEdge}` : undefined,
        borderRadius: reviewEdge ? 4 : undefined,
      }}
    >
      <Flex gap={COLUMN_GAP} style={{ alignItems: "center", width: "100%" }}>
        <Flex
          style={{
            width: COLUMN_WIDTH.student,
            flex: `0 0 ${COLUMN_WIDTH.student}px`,
            overflow: "hidden",
          }}
        >
          {student ? <ClickableStudentInfoRow student={student} /> : null}
        </Flex>

        <Flex
          gap={4}
          style={{
            width: COLUMN_WIDTH.currentSkill,
            flex: `0 0 ${COLUMN_WIDTH.currentSkill}px`,
            alignItems: "center",
          }}
        >
          <Select
            options={skillLevelOptions}
            style={{ width: COLUMN_WIDTH.skill, flexShrink: 0 }}
            size="small"
            placeholder="Current level"
            value={currentSkill || undefined}
            disabled={!canChangeCurrentLevel || currentLevelLocked}
            filterOption={filterFormOptions}
            showSearch
            onChange={(value) => {
              onCurrentSkill(value ?? "")
              setCurrentLevelUnlocked(false)
            }}
          />
          <Flex style={{ width: 24, flex: "0 0 24px" }}>
            {currentSkill && canChangeCurrentLevel ? (
              <Tooltip
                title={
                  currentLevelLocked
                    ? "Change current level"
                    : "Lock current level"
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={
                    currentLevelLocked
                      ? "Change current level"
                      : "Lock current level"
                  }
                  icon={<EditOutlined />}
                  onClick={() => setCurrentLevelUnlocked((open) => !open)}
                  style={{
                    width: 24,
                    height: 24,
                    color: currentLevelLocked ? undefined : theme.mainColor,
                  }}
                />
              </Tooltip>
            ) : null}
          </Flex>
        </Flex>

        <Select
          options={skillLevelOptions}
          style={{ width: COLUMN_WIDTH.skill, flexShrink: 0 }}
          size="small"
          placeholder="Next level"
          value={evaluation?.nextSkill}
          disabled={!canEdit || !currentSkill}
          filterOption={filterFormOptions}
          showSearch
          onChange={(value) => onNextSkill(value ?? "")}
        />

        <Flex
          center
          style={{
            width: COLUMN_WIDTH.tag,
            flex: `0 0 ${COLUMN_WIDTH.tag}px`,
            justifyContent: "flex-start",
          }}
        >
          {canClearNoEval ? (
            <Tooltip title="Clear no eval">
              <Tag
                color={tagColor}
                style={{ margin: 0, cursor: "pointer" }}
                onClick={() => onNoEval(false)}
              >
                {promotionTag}
              </Tag>
            </Tooltip>
          ) : (
            <Tag color={tagColor} style={{ margin: 0 }}>
              {promotionTag}
            </Tag>
          )}
        </Flex>

        <Flex
          style={{
            width: COLUMN_WIDTH.action,
            flex: `0 0 ${COLUMN_WIDTH.action}px`,
          }}
          gap={4}
        >
          {canEdit ? (
            <DisabledFieldTooltip
              disabled={submitDisabled}
              tooltip={submitTooltip}
            >
              <Button
                size="small"
                type="primary"
                disabled={submitDisabled}
                onClick={onSubmit}
              >
                Submit
              </Button>
            </DisabledFieldTooltip>
          ) : null}
          {canApprove ? (
            <DisabledFieldTooltip
              disabled={approveDisabled}
              tooltip={approveTooltip}
            >
              <Button
                size="small"
                icon={<CheckOutlined />}
                disabled={approveDisabled}
                onClick={onApprove}
                style={
                  approveDisabled
                    ? undefined
                    : {
                        backgroundColor: theme.eric.green,
                        borderColor: theme.eric.green,
                        color: theme.white,
                      }
                }
              />
            </DisabledFieldTooltip>
          ) : null}
          {canApprove && needsApprove ? (
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={onReject}
              style={{
                backgroundColor: theme.eric.red,
                borderColor: theme.eric.red,
                color: theme.white,
              }}
            />
          ) : null}
        </Flex>

        <Select
          options={teacherOptions}
          style={{ width: COLUMN_WIDTH.person, flexShrink: 0 }}
          size="small"
          placeholder="Assign a teacher"
          value={evaluation?.assignedTo}
          disabled={!canAssign}
          filterOption={filterFormOptions}
          allowClear
          showSearch
          onChange={(value) => onAssignTeacher(value ?? null)}
        />

        <Select
          options={approverOptions}
          style={{ width: COLUMN_WIDTH.person, flexShrink: 0 }}
          size="small"
          placeholder="Assign approver"
          value={evaluation?.approverId}
          disabled={!canAssign}
          filterOption={filterFormOptions}
          allowClear
          showSearch
          onChange={(value) => onAssignApprover(value ?? null)}
        />
      </Flex>

      {showHoldBlock ? (
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftSm}
          col
          gap={8}
          style={{
            width: "100%",
            backgroundColor: "#FFF6D8",
            border: `1px solid ${theme.warning}`,
            borderRadius: 6,
            padding: 8,
          }}
        >
          <Flex gap={16} style={{ alignItems: "center", flexWrap: "wrap" }}>
            <DisabledFieldTooltip
              disabled={noEvalBlocked}
              tooltip={studentLevelTooltip}
            >
              <Checkbox
                checked={false}
                disabled={!canEdit || noEvalBlocked}
                onChange={(event) => onNoEval(event.target.checked)}
              >
                No eval
              </Checkbox>
            </DisabledFieldTooltip>
            {NO_PROMO_REASONS.map((reason) => (
              <Checkbox
                key={reason}
                checked={evaluation?.noPromoReason?.includes(reason) ?? false}
                disabled={!canEdit}
                onChange={(event) =>
                  onToggleReason(reason, event.target.checked)
                }
              >
                {NO_PROMO_REASON_LABELS[reason]}
              </Checkbox>
            ))}
          </Flex>
          <Flex col gap={4} style={{ width: "100%" }}>
            <Typography.Text style={{ fontSize: 12 }}>
              Why no promotion
            </Typography.Text>
            <Input.TextArea
              rows={2}
              value={noteText}
              disabled={!canEdit}
              onChange={(event) => {
                const text = event.target.value
                setNoteText(text)
                if (!noteBurstStartedRef.current) {
                  onNoteEditingRef.current()
                  noteBurstStartedRef.current = true
                }
                debouncedOnNote(text)
              }}
            />
          </Flex>
        </AnimateInFlex>
      ) : null}
    </Flex>
  )
}

export default PromotionRow
